import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useSignOut } from "core";
import { scheduleAccountDeletion } from "core/features/profile/api";

export default function AccountDeletion() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { mutate: signOut } = useSignOut();

  const deleteAccount = async () => {
    setLoading(true);
    setError("");
    try {
      await scheduleAccountDeletion();
      signOut();
    } catch (err: any) {
      setError(err?.response?.data?.message || "We couldn't schedule account deletion. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/30 p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-300" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">Delete my account</h3>
          <p className="mt-1 text-sm text-red-800 dark:text-red-300">
            Your account will be scheduled for deletion. You can reactivate it by signing in within 30 days.
          </p>
          {error && <p className="mt-3 text-sm font-medium text-red-700 dark:text-red-300" role="alert">{error}</p>}
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Delete my account
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
          <div className="w-full max-w-md rounded-xl bg-surface p-6 shadow-xl">
            <h2 id="delete-account-title" className="text-lg font-semibold text-content">Delete your account?</h2>
            <p className="mt-2 text-sm text-content-secondary">This starts a 30-day grace period. After that, your account will be permanently purged.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" disabled={loading} onClick={() => setConfirmOpen(false)} className="rounded-lg border border-line-strong px-4 py-2 text-sm text-gray-700 dark:text-gray-300">Cancel</button>
              <button type="button" disabled={loading} onClick={deleteAccount} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Confirm deletion
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
